import { useMemo } from "react";
import { useLocation } from "react-router";

/**
 * Current route's `location.state` parsed with a runtime parser.
 *
 * @param parse - A **stable** function reference (module-level import or
 *   `useCallback`-wrapped). Passing an inline arrow function recreates the
 *   reference on every render and defeats the internal `useMemo`.
 *
 * @example
 *   const state = useRouterState(parseEventsRouterState);
 *
 *   // With a custom parser — wrap in useCallback to keep the reference stable:
 *   const parse = useCallback((s: unknown) => myParse(s), []);
 *   const state = useRouterState(parse);
 */
export function useRouterState<T>(
	parse: (state: unknown) => T | undefined,
): T | undefined {
	const { state } = useLocation();
	return useMemo(() => parse(state), [parse, state]);
}
